import { TextCollectionAttachment } from '@scale/shared/types/params/textcollection';
import { Button, Alert } from 'design-system';
import React, { useCallback, useRef, useEffect, useMemo, useState } from 'react';
import { TextInput, CircularProgress } from 'react-scale/components/design-system';
import { Paper } from 'react-scale/components/design-system/Paper';
import SanitizedMarkdown, {
  MARKDOWN_DIVIDER,
} from 'react-scale/components/design-system/SanitizedMarkdown';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { useChatbotContext } from 'react-scale/dashboard.scale.com/contexts/ChatbotRankingContext';
import API from '@scale/frontend-lib/dashboard-api';
import { SmartAssistantRlhfHook } from 'react-scale/components/scalers/useSmartAssistantRlhf';
import {
  DISABLE_PASTING_ERROR_TEXT,
  AUTOTYPER_DETECT_STEP,
  AUTOTYPER_DETECT_LENGTH,
  AUTOTYPER_DETECT_MAX_INTERVAL_MS,
} from 'react-scale/components/scalers/tasks/textcollection/CustomTextField';
import _ from 'lodash';
import debounce from 'lodash.debounce';
import { useLocalStorage } from 'react-use-v13';
import produce from 'immer';
import { compareTwoStrings } from 'string-similarity';
import { IRlhfPromptValidatorConfig } from '@scale/shared/types/rlhf';
import { AllowedRapidAttachmentManagerMetadata } from '@scale/shared/defaults';
import uuid from 'uuid';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
import { Breadcrumb } from '../breadcrumbs';

const CONTENTEDITABLE_ATTRIBUTE_NAME = 'contenteditable';
const DATA_GRAMM_ATTRIBUTE_NAME = 'data-gramm';
const MOUNT_SAPLING_AFTER_X_MS_NO_TYPING = 1500;
const MIN_WORDS_TO_MOUNT_SAPLING = 25;

interface Props {
  attachment: TextCollectionAttachment;
  projectId?: string;
  smartAssistantRlhfHook: SmartAssistantRlhfHook;
  assignmentId?: string;
  subtaskMetadata?: any;
  prefillPrompt?: string;
  prefillRefText?: string;
  setAnnotations?: (
    annotations: TextCollectionAnnotationsOrCallback,
    breadcrumbs?: Breadcrumb[],
  ) => void;
}

const ChatBotView: React.FC<Props> = (props: Props) => {
  const styles = useChatBotViewStyles();

  const { setRlhfPromptText } = props.smartAssistantRlhfHook;

  const chatbotContext = useChatbotContext();
  const chatBoxTexts = chatbotContext.chatBoxTexts;
  const assignmentId = props.assignmentId || uuid.v4();
  const [promptTexts, setPromptTexts] = useLocalStorage(
    'promptTexts' + assignmentId,
    _.range(chatBoxTexts.length).map(i => {
      if (i === 0 && props.prefillPrompt !== undefined) {
        return props.prefillPrompt;
      }
      if (i === 1 && props.prefillRefText !== undefined) {
        return props.prefillRefText;
      }
      return '';
    }),
  );

  const [submitting, setSubmitting] = useState(false);
  const [spellingAndGrammarCheckEnabled, setSpellingAndGrammarCheckEnabled] = useState(false);
  const [validatorConfig, setValidatorConfig] = useState<IRlhfPromptValidatorConfig | undefined>(
    undefined,
  );
  const [disablePasting, setDisablePasting] = useState(false);
  const [keystrokeIntervals, setKeystrokeIntervals] = React.useState<number[]>([]);
  const [lastKeystrokeTimestamp, setLastKeystrokeTimestamp] = React.useState<number | undefined>(
    undefined,
  );
  const [promptErrors, setPromptErrors] = useState<string[][]>(
    chatbotContext.chatBoxTexts.map(() => []),
  );
  const [promptWarnings, setPromptWarnings] = useState<string[]>([]);
  const [promptWithWarningOnly, setPromptWithWarningOnly] = useState<string | undefined>(undefined);

  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const isRangedMultiTurn = chatbotContext.isRangedMultiTurn;

  const answeringForm = chatbotContext.fieldsToShow.length > 0;
  const transcriptRef = useRef<HTMLDivElement>(null);
  const [Sapling, setSapling] = useState<any>(null);
  const [initialView, setInitialView] = useState(false);
  const validatePrompt = async () => {
    let validPrompt = true;
    let hasWarnings = false;
    if (!props.projectId) return { validPrompt, hasWarnings };
    const newErrors: string[][] = promptErrors.map(() => []);
    promptTexts.forEach((promptText, i) => {
      // empty check is for the first text field (prompt) only
      if (i === 0 && promptText.trim().length === 0) {
        newErrors.splice(i, 1, ['Please type a valid prompt.']);
        setPromptTexts(currentTexts =>
          produce(currentTexts, draft => {
            draft[i] = '';
          }),
        );

        validPrompt = false;
      }
      if (!validatorConfig?.similarPrompts?.enabled) return;
      const { threshold = 1 } = validatorConfig.similarPrompts;
      for (let j = i + 1; j < promptTexts.length; j++) {
        const similarity = compareTwoStrings(promptTexts[i].trim(), promptTexts[j].trim());
        if (similarity > threshold) {
          newErrors.splice(i, 1, ['Please type less similar prompts.']);
          validPrompt = false;
        }
      }
    });
    setPromptErrors(newErrors);

    await Promise.all(
      promptTexts.map(async (promptText, i) => {
        // i = 0 is always the prompt
        // i = 1 is the reference text (if it exists)
        const chatBoxText = chatbotContext.chatBoxTexts[i];
        const { errors, warnings } =
          i === 0
            ? await API.selfserve.validatePrompt({
                promptText,
                projectId: props.projectId!,
                chatBoxText,
                assignmentId: props.assignmentId,
                ...(promptTexts.length > 1 && { referenceText: promptTexts[1] }),
              })
            : await API.selfserve.validateReferenceText({
                promptText,
                projectId: props.projectId!,
                chatBoxText,
                assignmentId: props.assignmentId,
              });
        if (errors.length > 0) {
          validPrompt = false;
          newErrors.splice(i, 1, [...newErrors[i], ...errors]);
        } else {
          newErrors.splice(i, 1, newErrors[i]);
        }

        if (warnings.length > 0) {
          setPromptWarnings(warnings);
          hasWarnings = true;
        }
      }),
    );
    setPromptErrors(newErrors);

    return { validPrompt, hasWarnings };
  };
  const onEnter = async () => {
    setSubmitting(true);
    const ref = transcriptRef?.current;
    if (ref) setTimeout(() => (ref.scrollTop = ref.scrollHeight + 1000), 1000);

    try {
      if (!promptWithWarningOnly && !chatbotContext.skipPromptValidation) {
        if (promptTexts.every((promptText: string) => promptText === '')) {
          setSubmitting(false);
          return;
        }

        const { validPrompt, hasWarnings } = await validatePrompt();
        if (!validPrompt) {
          setSubmitting(false);
          return;
        }
        // if there's no errors, but there are warnings, the prompt is valid and can be submitted again
        if (hasWarnings) {
          setSubmitting(false);
          setPromptWithWarningOnly(promptTexts[0]);
          return;
        }
      }
      await chatbotContext.submitPrompts(promptTexts, props.attachment.content);
    } catch (_e) {
      setSubmissionError(
        'Error generating response. You may try again in 10 seconds. If you have already retried twice, please skip this task.',
      );
      setTimeout(() => {
        setSubmissionError(undefined);
        setSubmitting(false);
      }, 10000);
      return;
    }

    if (ref) setTimeout(() => (ref.scrollTop = ref.scrollHeight + 1000), 2500);
    if (!chatbotContext.rlhfParams?.isFair || isRangedMultiTurn) {
      setPromptTexts(Array(chatbotContext.chatBoxTexts.length).fill(''));
    }
    setSubmitting(false);
  };

  const onReset = async () => {
    if (!answeringForm && !isRangedMultiTurn) return;
    if (props.setAnnotations) {
      props.setAnnotations({});
    }
    chatbotContext.resetChat();
  };

  const onUndo = () => {
    if (props.setAnnotations && chatbotContext.resetLastInteraction()) {
      props.setAnnotations(chatbotContext.resetLastInteraction()!);
    }
    chatbotContext.handleUndo();
  };

  useEffect(() => {
    if (chatbotContext.transcript) {
      if (!initialView) {
        // don't wanna auto scroll on first load of task
        setInitialView(true);
        return;
      }
    }
  }, [chatbotContext.transcript]);

  // Dynamic Import of Sapling
  useEffect(() => {
    import('@scale/frontend-lib/sapling-module')
      .then(mod => {
        setSapling(mod.default);
        return;
      })
      .catch(_ => {
        setSapling(null);
      });
  }, []);

  useEffect(() => {
    if (promptTexts?.[0].length) setRlhfPromptText(promptTexts[0]);
  }, [promptTexts, setRlhfPromptText]);

  const mountSaplingOnEditor = useCallback(() => {
    const editor = document.querySelector(`textarea[name='promptInput']`) as HTMLElement;
    if (!editor || !Sapling) {
      return;
    }
    if (!editor.hasAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME)) {
      // apply Sapling to prompt input for spelling/grammar check
      editor.setAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME, 'true');
      editor.setAttribute(DATA_GRAMM_ATTRIBUTE_NAME, 'false');
      Sapling.observe(editor);
    }
  }, [Sapling]);

  const unmountSaplingFromEditor = useCallback(() => {
    const editor = document.querySelector(`textarea[name='promptInput']`) as HTMLElement;
    if (!editor || !Sapling) {
      return;
    }
    if (editor.hasAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME)) {
      editor.removeAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME);
      editor.removeAttribute(DATA_GRAMM_ATTRIBUTE_NAME);
      Sapling.unobserve(editor);
    }
  }, [Sapling]);

  useEffect(() => {
    async function execute() {
      if (props.projectId && Sapling) {
        const { enabled } = await API.selfserve.fetchSpellingAndGrammarEnabled({
          projectId: props.projectId,
        });
        setSpellingAndGrammarCheckEnabled(enabled);
      }
    }
    execute();
  }, [props.projectId, Sapling]);

  useEffect(() => {
    async function fetchDisablePasting() {
      if (props.projectId && Sapling) {
        const { enabled: disablePastingEnabled } = await API.selfserve.fetchDisablePastingEnabled({
          projectId: props.projectId,
        });
        setDisablePasting(disablePastingEnabled);
      }
    }
    fetchDisablePasting();
  }, [props.projectId, Sapling]);

  useEffect(() => {
    async function execute() {
      if (props.projectId) {
        const validatorConfig = await API.selfserve.fetchRlhfValidatorConfig({
          projectId: props.projectId,
        });
        setValidatorConfig(validatorConfig);
      }
    }
    execute();
  }, [props.projectId]);

  useEffect(() => {
    if (disablePasting || spellingAndGrammarCheckEnabled) {
      if (chatbotContext.isDone || submitting) {
        unmountSaplingFromEditor();
      } else if (!chatbotContext.isDone) {
        mountSaplingOnEditor();
      }
    }
  }, [
    disablePasting,
    spellingAndGrammarCheckEnabled,
    chatbotContext.isDone,
    submitting,
    mountSaplingOnEditor,
    unmountSaplingFromEditor,
  ]);

  useEffect(() => {
    if (!disablePasting) {
      return;
    }
    if (lastKeystrokeTimestamp) {
      const detectAutotyper = setTimeout(() => {
        const numKeystrokes = keystrokeIntervals.length;
        let autotyperDetected = false;
        for (let step = 0; step < numKeystrokes; step = step + AUTOTYPER_DETECT_STEP) {
          if (step + AUTOTYPER_DETECT_LENGTH > numKeystrokes) {
            break;
          }
          const subset = keystrokeIntervals.slice(step, step + AUTOTYPER_DETECT_LENGTH);

          const avgInterval = _.mean(subset) ?? 0.0;
          if (avgInterval > AUTOTYPER_DETECT_MAX_INTERVAL_MS) {
            continue;
          }
          if (avgInterval / 2 > (_.max(subset) ?? 0.0) - (_.min(subset) ?? 0.0)) {
            autotyperDetected = true;
            break;
          }
        }
        if (autotyperDetected) {
          setPromptTexts(promptTexts.map(() => ''));
          setPromptErrors(promptErrors.map(() => [DISABLE_PASTING_ERROR_TEXT]));
        }
        setLastKeystrokeTimestamp(undefined);
        setKeystrokeIntervals([]);
      }, 3 * 1000);
      return () => clearTimeout(detectAutotyper);
    }
  }, [
    disablePasting,
    keystrokeIntervals,
    lastKeystrokeTimestamp,
    promptErrors,
    promptTexts,
    setPromptTexts,
  ]);

  const topic = props.attachment.content || 'Start the conversation!';

  const text = `${MARKDOWN_DIVIDER}\n --- \n ## Topic \n \n ${topic} \n \n --- \n ## CONVERSATION \n \n ${
    chatbotContext.transcript || ''
  }`;

  const debouncedMountSapling = useMemo(
    () => debounce(mountSaplingOnEditor, MOUNT_SAPLING_AFTER_X_MS_NO_TYPING),
    [mountSaplingOnEditor],
  );

  const onChange = useMemo(() => {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, i: number) => {
      if (promptWithWarningOnly && event.target.value !== promptWithWarningOnly) {
        setPromptWithWarningOnly(undefined);
      }

      unmountSaplingFromEditor();
      // only can disable pasting on the prompt field (i = 0)
      if (disablePasting && i === 0) {
        // Detecting autotyper
        const timestamp = event.nativeEvent.timeStamp;
        if (lastKeystrokeTimestamp) {
          setKeystrokeIntervals([...keystrokeIntervals, timestamp - lastKeystrokeTimestamp]);
        }
        setLastKeystrokeTimestamp(timestamp);
        setPromptTexts(currentTexts =>
          produce(currentTexts, draft => {
            draft[i] = '';
          }),
        );
      }
      setPromptTexts(currentTexts =>
        produce(currentTexts, draft => {
          draft[i] = event.target.value;
        }),
      );

      const numWords = event.target.value.split(' ').length;
      if (numWords > MIN_WORDS_TO_MOUNT_SAPLING) {
        debouncedMountSapling();
      }
    };
  }, [
    debouncedMountSapling,
    unmountSaplingFromEditor,
    chatbotContext.chatBoxTexts,
    disablePasting,
    keystrokeIntervals,
    lastKeystrokeTimestamp,
    promptTexts,
    setPromptTexts,
  ]);

  const promptWordsTip = useMemo(() => {
    if (!validatorConfig?.wordCount?.enabled) return '';
    const subtaskMetadata = props.subtaskMetadata?.metadataVariableValues;
    const [minResponseLength, maxResponseLength] = [
      subtaskMetadata?.[AllowedRapidAttachmentManagerMetadata.MinResponseLength],
      subtaskMetadata?.[AllowedRapidAttachmentManagerMetadata.MaxResponseLength],
    ];
    let { min = 1, max } = validatorConfig?.wordCount;
    if (minResponseLength) min = minResponseLength;
    if (maxResponseLength) max = maxResponseLength;
    return max
      ? `; Prompt should have between ${min} and ${max} words.`
      : `; Prompt should have at least ${min} words.`;
  }, [validatorConfig, props.subtaskMetadata]);

  const referenceWordsTip = useMemo(() => {
    if (!validatorConfig?.referenceTextWordCount?.enabled) return '';
    const { min = 1, max } = validatorConfig?.referenceTextWordCount;
    return max
      ? `; Text should have between ${min} and ${max} words.`
      : `; Text should have at least ${min} words.`;
  }, [validatorConfig]);

  return (
    <Paper className={styles.textAttachment}>
      <div className={styles.chatbotTranscript} ref={transcriptRef}>
        <SanitizedMarkdown text={text} addBackgroundToTransparentImages disableFuzzyLinks />
        {submitting && (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        )}
      </div>
      <div className={styles.chatbotPrompt}>
        {!chatbotContext.hideReviewNav ? (
          <div>
            Navigate the chat history:
            <Button
              className={styles.chatbotPromptButton}
              disabled={!chatbotContext.previousInteraction}
              type="primary"
              onClick={async () => {
                chatbotContext.previousInteraction && chatbotContext.previousInteraction();
              }}
            >
              Back
            </Button>
            <Button
              className={styles.chatbotPromptButton}
              disabled={!chatbotContext.nextInteraction}
              type="primary"
              onClick={async () => {
                chatbotContext.nextInteraction && chatbotContext.nextInteraction();
              }}
            >
              Next
            </Button>
            <Button
              className={styles.chatbotPromptButton}
              disabled={!chatbotContext.nextInteraction}
              type="primary"
              onClick={async () => {
                chatbotContext.jumpToEnd && chatbotContext.jumpToEnd();
              }}
            >
              End &gt;
            </Button>
            {chatbotContext.reviewNoBranching && (
              <Button
                className={styles.chatbotPromptButton}
                disabled={!chatbotContext.nextInteraction}
                type="primary"
                onClick={async () => {
                  chatbotContext.startBranchConversation &&
                    chatbotContext.startBranchConversation();
                }}
              >
                Restart Conversation From This Point
              </Button>
            )}
          </div>
        ) : (
          <>
            <div style={{ width: '100%' }}>
              {submissionError && (
                <Alert severity="error" style={{ marginBottom: 10 }}>
                  {submissionError}
                </Alert>
              )}
              {chatBoxTexts.map((text, i) => (
                <div style={{ marginTop: 4 }} key={i}>
                  {promptErrors[i].map((err, j) => (
                    <Alert
                      key={j}
                      severity="error"
                      style={{ marginBottom: 10 }}
                      onClose={() => {
                        const newErrors = [...promptErrors];
                        newErrors[i].splice(j, 1);
                        setPromptErrors(newErrors);
                      }}
                    >
                      {err}
                    </Alert>
                  ))}
                  {i === 0 &&
                    promptWarnings.map((warning, j) => (
                      <Alert
                        key={j}
                        severity="warning"
                        style={{ marginBottom: 10 }}
                        onClose={() => {
                          const newWarnings = [...promptWarnings];
                          newWarnings.splice(j, 1);
                          setPromptWarnings(newWarnings);
                        }}
                      >
                        {warning}
                      </Alert>
                    ))}
                  <TextInput
                    onPaste={event => {
                      if (disablePasting && i === 0) {
                        const newErrors = [...promptErrors];
                        newErrors.splice(i, 1, [DISABLE_PASTING_ERROR_TEXT]);
                        setPromptErrors(newErrors);
                        event.preventDefault();
                      }
                    }}
                    multiline={true}
                    maxRows={3}
                    name={'promptInput'}
                    fullWidth
                    disabled={submitting || answeringForm || chatbotContext.isDone}
                    value={promptTexts[i]}
                    label={
                      promptErrors[i]?.length > 0
                        ? 'Please fix prompt errors'
                        : answeringForm
                        ? 'Answer the questions to the right'
                        : text
                    }
                    size={'large'}
                    onChange={e => onChange(e, i)}
                    error={promptErrors[i]?.length > 0}
                    minHeight="100px"
                    helperText={`Word Count: ${_.words(promptTexts[i]).length}${
                      i === 0 ? promptWordsTip : referenceWordsTip
                    }`}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <Button
                className={styles.chatbotPromptButton}
                disabled={
                  promptTexts[0].length === 0 ||
                  submitting ||
                  answeringForm ||
                  chatbotContext.isDone
                }
                type="primary"
                onClick={onEnter}
              >
                Enter
              </Button>
              {isRangedMultiTurn && (
                <Button
                  className={styles.chatbotPromptButton}
                  onClick={onUndo}
                  disabled={chatbotContext.history.interactions.length === 0}
                >
                  Undo
                </Button>
              )}
              {(chatbotContext.rlhfParams?.isFair ||
                isRangedMultiTurn ||
                chatbotContext.canResetChat) && (
                <Button
                  className={styles.chatbotPromptButton}
                  disabled={chatbotContext.isStreaming || (!answeringForm && !isRangedMultiTurn)}
                  type="primary"
                  onClick={onReset}
                >
                  {isRangedMultiTurn ? 'Restart' : 'Undo'}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Paper>
  );
};

const useChatBotViewStyles = makeStyles({
  chatbotTranscript: {
    height: 'calc(100vh - 420px)',
    overflowY: 'scroll',
    marginBottom: 16,
  },
  chatbotPrompt: {
    width: '100%',
    display: 'flex',
  },
  chatbotPromptButton: {
    marginLeft: 8,
    marginTop: 7,
  },
  textAttachment: {
    margin: 24,
    padding: 32,
    lineHeight: 1.6,
    '& tr': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
    },
    '& tr:nth-child(even)': {
      backgroundColor: '#f2f2f2',
    },
    '& th': {
      border: '1px solid rgb(0, 0, 0, 0.3)',
      padding: '6px 10px',
      backgroundColor: '#f2f2f2',
    },
    '& td': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
      padding: '10px',
    },
  },
});

export default ChatBotView;
